import { create } from 'zustand';

export interface TaskChunk {
  content: string;
  timestamp: number;
}

export interface BackgroundTask {
  id: string;
  taskId: string;
  requestId?: string; // SDK 返回的 requestId，用于取消任务
  resourceType: string;
  resourceId: number;
  resourceName: string;
  status: 'pending' | 'generating' | 'done' | 'error';
  error?: string;
  progress: number;
  statusText: string;
  createdAt: number;
  onComplete?: (content: string) => void;
  onDone?: (result?: any) => void | Promise<void>; // 完成后的回调
  navigationUrl?: string; // 完成后查看的链接
  chunks?: TaskChunk[]; // 流式生成的历史记录
}

interface TaskStore {
  tasks: BackgroundTask[];
  activeTaskId: string | null; // 当前在前台显示的任务ID
  backgroundedTaskIds: Set<string>; // 被后台化的任务ID集合
  addTask: (task: BackgroundTask) => void;
  updateTask: (id: string, updates: Partial<BackgroundTask>) => void;
  removeTask: (id: string) => void;
  getTask: (id: string) => BackgroundTask | undefined;
  addChunk: (id: string, chunk: TaskChunk) => void;
  setActiveTask: (id: string | null) => void;
  moveToBackground: (id: string) => void;
  markAsBackgrounded: (id: string) => void;
}

export const useTaskStore = create<TaskStore>()(
  (set, get) => ({
      tasks: [],
      activeTaskId: null,
      backgroundedTaskIds: new Set<string>(),

      addTask: (task) => {
        set((state) => ({
          tasks: [...state.tasks, { ...task, chunks: task.chunks || [] }],
          activeTaskId: task.id, // 新任务默认显示在前台
        }));
      },

      updateTask: (id, updates) =>
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id ? { ...t, ...updates } : t
          ),
        })),

      removeTask: (id) => {
        set((state) => {
          const newBackgroundedTaskIds = new Set(state.backgroundedTaskIds);
          newBackgroundedTaskIds.delete(id);
          return {
            tasks: state.tasks.filter((t) => t.id !== id),
            activeTaskId: state.activeTaskId === id ? null : state.activeTaskId,
            backgroundedTaskIds: newBackgroundedTaskIds,
          };
        });
      },

      getTask: (id) => get().tasks.find((t) => t.id === id),

      addChunk: (id, chunk) =>
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id
              ? { ...t, chunks: [...(t.chunks || []), chunk] }
              : t
          ),
        })),

      setActiveTask: (id) => set({ activeTaskId: id }),

      moveToBackground: (id) => {
        set((state) => ({
          activeTaskId: state.activeTaskId === id ? null : state.activeTaskId,
        }));
      },

      markAsBackgrounded: (id) => {
        set((state) => {
          const newBackgroundedTaskIds = new Set(state.backgroundedTaskIds);
          newBackgroundedTaskIds.add(id);
          return {
            backgroundedTaskIds: newBackgroundedTaskIds,
          };
        });
      },
    })
);
